import { useContext } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import type { TimeseriesMetric } from '@/pcs/domain/entities/pcs-timeseries';
import { IPCSRepository } from '@/pcs/domain/repositories/pcs.repository';
import { PCSOverviewContext } from '@/pcs/view/pages/pcs-overview';
import { GetPowerAvailability } from '@/pcs/view/queries/get-power-availability';
import { formatNumber } from '@/utils/format/number';

import { PlotSkeleton } from '../../skeletons/PlotSkeleton';
import ChartInfoBox from '../chart-info-box';
import { PlotSection } from '../plot-section';

import { getBaseChartOptions } from './../common-chart-config';
import {
  getReferencePlotBands,
  type ReferenceArea,
} from './../reference-area-helper';
// Series & config imports
import { generatePowerAvailabilitySeries } from './power-availability-series';

export type GetPowerAvailabilityPlotProps = {
  filters: IPCSRepository.Filters;
};

export function GetPowerAvailabilityPlot({
  filters,
}: GetPowerAvailabilityPlotProps) {
  const asset = useContext(PCSOverviewContext);

  // NOTE: assertion for asset here, as we assume the asset is always available
  const powerAvailabilityResult = GetPowerAvailability.useQuery({
    filters,
    assetFullFrontendId: asset!.fullFrontendId,
  });

  if (powerAvailabilityResult.isLoading) {
    return <PlotSkeleton sectionName="PCS Efficiency" />;
  }
  if (powerAvailabilityResult.isError) {
    return <div>Error loading data</div>;
  }

  const dataSection: TimeseriesMetric = powerAvailabilityResult.data;
  const series = generatePowerAvailabilitySeries(dataSection);

  // Add reference areas for `ok`, `warning`, `critical`
  const referenceAreas: ReferenceArea[] = [
    // Reverse might mean "from" is higher than "to" if yAxis is reversed, so watch that
    { lower: 100, upper: 98, state: 'ok' },
    { lower: 98, upper: 90, state: 'info' },
    { lower: 90, upper: 0, state: 'warning' },
  ];

  const plotBands = getReferencePlotBands(referenceAreas);

  // Build your base config & override or extend as needed
  const baseConfig = getBaseChartOptions({
    title: `${dataSection.metricName} Over Time`,
    xAxisTitle: 'Time',
    yAxisTitle: 'Availability (%)',
    plotBands: plotBands,
  });

  const chartOptions: Highcharts.Options = {
    ...baseConfig,
    series,
    // Example of customizing the tooltip for this chart only:
    yAxis: {
      ...baseConfig.yAxis,
    },
    tooltip: {
      pointFormatter() {
        return `${this.series.name}: <strong>${formatNumber(Number(this.y), 1)} %</strong>`;
      },
    },
  };

  return (
    <PlotSection
      sectionName={dataSection.metricName}
      sectionStatus={dataSection.metricStatus}
    >
      <div className="flex flex-row gap-x-10">
        <div className="min-h-[400px] flex-1 sm:w-3/4">
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            className="w-full"
          />
        </div>

        <ChartInfoBox
          description={`Power availability is determined by the operational status of the PCS.
            It indicates how often the PCS is functioning correctly (OK) compared to instances
            where data is missing or invalid (gap/invalid).
            <br/>
            <br/>
            A high percentage of OK status suggests stable performance, whereas frequent gaps may
            point to connectivity or measurement issues.`}
        />
      </div>
    </PlotSection>
  );
}
